import { defineAsyncComponent, type Plugin } from 'vue';
import type { PartialBy } from '@/components/types';

import Core from '@/core/core/core';
import coreDefaultOptions from '@/core/core/options';

import * as Forms from '../../components/forms/forms';

const TopNotice = defineAsyncComponent(() => import('@/components/core/notice/notice.vue'));

import type { Options as TopPopupOptions } from '@/components/popup/lib/worker';
import TopPopupWorker from '@/components/popup/lib/worker';
import TopPopupWorkerGlobalEvents from '@/components/popup/lib/worker.globalEvents';

import TopDialogs from '@/components/dialog/dialog/dialogs/dialogs.vue';
import { TopDialogWorker } from '@/components/dialog/lib/worker';
import type { TopDialogOptions } from '@/components/dialog/lib/types';

import directiveData from '@/core/directives/data';
import directiveFocus from '@/core/directives/focus';
import directiveScrollIntoView from '@/core/directives/scrollIntoView';
import directiveScrollShadow from '@/core/directives/scrollShadow';
import directiveSticky from '@/core/directives/sticky';
import directiveSwimUp from '@/core/directives/swimUp';
import directiveTooltip from '@/core/directives/tooltip';

declare global {
	const vd: typeof console.log;
}

window['vd'] = console.log;

declare module '@vue/runtime-core' {
	interface ComponentCustomProperties {
		/**
		 * Статический класс с текущим состоянимем UI
		 */
		$core: typeof Core;

		$vd: typeof console.log;
	}

	/**
	 * Обязательные компоненты UI
	 *
	 * Они подключаются через плагин и доступны без явного указания импорта
	 */
	export interface GlobalComponents {
		TopAvatar: typeof Forms.TopAvatar;
		TopButton: typeof Forms.TopButton;
		TopCheckbox: typeof Forms.TopCheckbox;
		TopControlLabel: typeof Forms.TopControlLabel;
		TopHint: typeof Forms.TopHint;
		TopInput: typeof Forms.TopInput;
		TopInputDate: typeof Forms.TopInputDate;
		TopInputRange: typeof Forms.TopInputRange;
		TopRadio: typeof Forms.TopRadio;
		TopSwitcher: typeof Forms.TopSwitcher;
		TopTextarea: typeof Forms.TopTextarea;
		TopSelect: typeof Forms.TopSelect;
		TopLoadbar: typeof Forms.TopLoadbar;

		TopNotice: typeof TopNotice;
		TopDialogs: typeof TopDialogs;
	}
}

type CoreOptions = PartialBy<typeof coreDefaultOptions, 'gmt' | 'documentClassModificators'>;

type Options = CoreOptions & {
	topPopupOptions?: TopPopupOptions
	topDialogOptions?: TopDialogOptions
};

/**
 * Плагин для интеграции UI во Vue приложение
 *
 * - В глобальную область видимости шаблонов будет добавлен объект $core
 * - Будут зарегистрирвоаны директивы UI: https://ui.topvisor.com/?path=/docs/core-directives-focus--docs
 * - Будут подключены базовые компоненты UI/Forms: https://ui.topvisor.com/?path=/docs/components-forms-readme--docs
 */
export default {

	install: (app, options: Options) => {
		app.config.globalProperties.$core = Core;
		app.config.globalProperties.$vd = console.log;

		// начальные настройки UI
		for (const name in options) {
			Core.state[name] = options[name];
		}

		if (options.widthForMobile) Core.widthForMobile = options.widthForMobile;
		if (options.themeName) Core.themeName = options.themeName;

		Core._setState();

		if (options.topPopupOptions) TopPopupWorker.options = options.topPopupOptions;
		TopPopupWorkerGlobalEvents.init();

		TopDialogWorker.init(options.topDialogOptions);

		// определение директив
		app.directive('top-data', directiveData);
		app.directive('top-focus', directiveFocus);
		app.directive('top-scroll-into-view', directiveScrollIntoView);
		app.directive('top-scroll-shadow', directiveScrollShadow);
		app.directive('top-sticky', directiveSticky);
		app.directive('top-swim-up', directiveSwimUp);
		app.directive('top-tooltip', directiveTooltip);

		// определение базовых компонентов
		app.component('TopAvatar', Forms.TopAvatar);
		app.component('TopButton', Forms.TopButton);
		app.component('TopCheckbox', Forms.TopCheckbox);
		app.component('TopControlLabel', Forms.TopControlLabel);
		app.component('TopHint', Forms.TopHint);
		app.component('TopInput', Forms.TopInput);
		app.component('TopInputDate', Forms.TopInputDate);
		app.component('TopInputRange', Forms.TopInputRange);
		app.component('TopLoadbar', Forms.TopLoadbar);
		app.component('TopRadio', Forms.TopRadio);
		app.component('TopSelect', Forms.TopSelect);
		app.component('TopSwitcher', Forms.TopSwitcher);
		app.component('TopTextarea', Forms.TopTextarea);

		app.component('TopNotice', TopNotice);
		app.component('TopDialogs', TopDialogs);
	},

} satisfies Plugin<Options>;
